import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Users,
  FolderKanban,
  Plus,
  LogOut,
  User,
  ChevronDown,
  Home,
  Settings,
  HelpCircle,
  TrendingUp,
  Shield,
  Phone,
  BookOpen,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useRole } from "../contexts/RoleContext";
import salesBotLogo from "../../assets/sales_bot_logo_transparent.svg";

function Header({ currentTab, setCurrentTab, user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { role, permissions } = useRole();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const dropdownRef = useRef(null);

  const handleTabChange = (tab) => {
    const path = `/${tab}`;
    if (window.__meetingLeaveGuard) {
      window.__meetingLeaveGuard(path);
      return;
    }
    setCurrentTab(tab);
    navigate(path);
  };

  const isActive = (path) => {
    return (
      location.pathname === `/${path}` ||
      (location.pathname === "/" && path === "meetings")
    );
  };

  const handleLogout = async () => {
    try {
      await logout();
      setShowUserMenu(false);
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header className="border-b border-gray-200 backdrop-blur-md sticky top-0 z-40 bg-white shadow-sm">
      <div className="max-w-7xl mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-4">
        {/* Logo/Brand */}
        <div className="flex items-center gap-3">
          <img
            src={salesBotLogo}
            alt="Sales Bot Logo"
            style={{
              width: "140px",
              height: "auto",
            }}
          />
          <h1 className="text-xl font-bold text-gray-800 tracking-tight">
            Sales Bot
          </h1>
        </div>

        {/* Navigation */}
        <nav className="flex flex-wrap items-center gap-2">
          <button
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
              isActive("meetings")
                ? "text-white shadow-sm"
                : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
            }`}
            style={isActive("meetings") ? { backgroundColor: "#ef5366" } : {}}
            onClick={() => handleTabChange("meetings")}
          >
            <Users size={18} />
            <span>Meetings</span>
          </button>
          <button
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
              isActive("groups")
                ? "text-white shadow-sm"
                : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
            }`}
            style={isActive("groups") ? { backgroundColor: "#ef5366" } : {}}
            onClick={() => handleTabChange("groups")}
          >
            <FolderKanban size={18} />
            <span>Groups</span>
          </button>
          <button
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
              isActive("knowledge-base")
                ? "text-white shadow-sm"
                : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
            }`}
            style={
              isActive("knowledge-base") ? { backgroundColor: "#ef5366" } : {}
            }
            onClick={() => handleTabChange("knowledge-base")}
          >
            <BookOpen size={18} />
            <span>Knowledge Base</span>
          </button>
          <button
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
              isActive("new-meeting")
                ? "text-white shadow-sm"
                : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
            }`}
            style={
              isActive("new-meeting") ? { backgroundColor: "#ef5366" } : {}
            }
            onClick={() => handleTabChange("new-meeting")}
          >
            <Plus size={18} />
            <span>New Meeting</span>
          </button>
          {/* Calling Agent - Hidden for now */}
          {/* <button
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
              isActive("calling-agent")
                ? "text-white shadow-sm"
                : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
            }`}
            style={
              isActive("calling-agent") ? { backgroundColor: "#ef5366" } : {}
            }
            onClick={() => handleTabChange("calling-agent")}
          >
            <Phone size={18} />
            <span>Calling Agent</span>
          </button> */}
          {/* Admin Panel - Only for Global Admins */}
          {role === "global_admin" && (
            <button
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                isActive("admin")
                  ? "text-white shadow-sm"
                  : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
              }`}
              style={isActive("admin") ? { backgroundColor: "#ef5366" } : {}}
              onClick={() => handleTabChange("admin")}
            >
              <Shield size={18} />
              <span>Admin Panel</span>
            </button>
          )}
        </nav>

        {/* User Menu */}
        {user && (
          <div className="relative" ref={dropdownRef}>
            <button
              className="flex items-center gap-3 px-4 py-2 rounded-lg bg-white hover:bg-gray-100 transition-all border border-gray-200"
              onClick={() => setShowUserMenu(!showUserMenu)}
            >
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: "#ef5366" }}
              >
                <User size={16} className="text-white" />
              </div>
              <span className="text-sm font-medium text-gray-800">
                {user.name || user.displayName || user.username || "User"}
              </span>
              <ChevronDown
                size={16}
                className={`text-gray-600 transition-transform ${
                  showUserMenu ? "rotate-180" : ""
                }`}
              />
            </button>

            {showUserMenu && (
              <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl shadow-xl p-2 border border-gray-200">
                <div className="p-4 flex items-center gap-3 border-b border-gray-200">
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center shadow-md"
                    style={{ backgroundColor: "#ef5366" }}
                  >
                    <User size={24} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-gray-800 truncate">
                      {user.name || user.displayName || "User"}
                    </div>
                    {user.username && (
                      <div className="text-sm text-gray-600 truncate">
                        {user.username}
                      </div>
                    )}
                  </div>
                </div>
                {/* My Report - Only show if user has permission */}
                {permissions?.can_view_my_report && (
                  <button
                    className="w-full mt-2 px-4 py-2.5 text-left text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg flex items-center gap-3 transition-colors"
                    onClick={() => {
                      setShowUserMenu(false);
                      if (window.__meetingLeaveGuard) {
                        window.__meetingLeaveGuard("/my-analytics");
                        return;
                      }
                      navigate("/my-analytics");
                    }}
                  >
                    <TrendingUp size={18} />
                    <span>My Report</span>
                  </button>
                )}
                <button
                  className="w-full mt-2 px-4 py-2.5 text-left text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg flex items-center gap-3 transition-colors"
                  onClick={() => {
                    setShowUserMenu(false);
                    navigate("/extension-access");
                  }}
                >
                  <Settings size={18} />
                  <span>Extension Access</span>
                </button>
                <button
                  className="w-full mt-1 px-4 py-2.5 text-left text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-3 transition-colors"
                  onClick={handleLogout}
                >
                  <LogOut size={18} />
                  <span>Sign Out</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}

export default Header;
